import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Section } from '../fx/AccentContext';
import { MaskedLines } from '../fx/MaskedLines';
import LivingImage from '../fx/LivingImage';
import { SectionLabel, TextLink, PADX } from './ui';
import { EASE_EXPO, EASE_QUINT } from '../fx/ease';

// 採用への入口（2026-10-18 代表指示: 職種別の For Engineers / For Business ではなく、
// 会社紹介資料・バリュー・仕事探しの3つにする。バリューは採用サイトの Values の欄へ送る＝2026-10-20）
const rows = [
  {
    no: '01',
    en: 'COMPANY DECK',
    jp: '会社紹介資料',
    to: '/careers/deck',
    desc: 'ビジョン、事業、Trooper Core、チームと働き方。採用向けの会社紹介資料をそのまま読めます。',
    img: '/img/architecture/green/p05-green-beam-underside.jpg',
  },
  {
    no: '02',
    en: 'VALUES',
    jp: '毎日自分に問う基準',
    to: '/careers/values',
    desc: '徹底してやり切れ。上司の仕事を喰え。覚悟を持って任せろ。成長に貪欲になれ。キャップをかけるな。',
    img: '/img/architecture/refined/r02-green-wall-muted-sky.jpg',
  },
  {
    no: '03',
    en: 'FIND YOUR JOB',
    jp: 'あなたに合う仕事を探す',
    to: '/careers/positions',
    desc: 'やりたいこと、大事にしたいこと、将来やりたいことを書くと、合いそうな仕事をご案内します。',
    img: '/img/architecture/green/p08-green-courtyard-panel.jpg',
  },
];

const CareersSection = () => {
  const [active, setActive] = useState(0);
  const reduce = useReducedMotion();

  return (
    <Section id="careers" index={2} accent="green" className="border-t border-line">
      <div className={`${PADX} py-24 md:py-32`}>
        <div className="grid grid-cols-12 gap-y-14 lg:gap-x-10">
          <div className="col-span-12 lg:col-span-6">
            <SectionLabel no="03">Careers</SectionLabel>
            <MaskedLines
              as="h2"
              per="char"
              lines={['BUILD', 'BEYOND', 'LIMITS']}
              className="display-type mt-8 text-[14vw] text-ink sm:text-8xl lg:text-[6.2vw] 2xl:text-[104px]"
              stagger={0.12}
            />
            <MaskedLines
              lines={[['人生の勝負所に、', '本気で向き合う人へ。']]}
              className="mt-7 text-[15px] font-medium leading-relaxed tracking-display-ja text-gray-700 md:text-[17px]"
              delay={0.3}
            />

            <div className="mt-10 border-t border-line md:mt-12">
              {rows.map((v, i) => (
                <motion.div
                  key={v.no}
                  initial={reduce ? false : { opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-8%' }}
                  transition={{ duration: 0.7, ease: EASE_EXPO, delay: i * 0.06 }}
                >
                  <Link
                    to={v.to}
                    data-cursor="JOIN"
                    className="group block border-b border-line py-5"
                    onPointerEnter={(e) => {
                      if (e.pointerType === 'mouse') setActive(i);
                    }}
                  >
                    <div className="flex items-baseline gap-5 md:gap-7">
                      <span
                        className={`text-[12px] font-semibold tracking-label tnum transition-colors duration-300 ${
                          active === i ? 'text-live' : 'text-gray-500'
                        }`}
                      >
                        {v.no}
                      </span>
                      <span
                        className={`display-type text-xl text-ink transition-transform duration-500 ease-expo md:text-2xl ${
                          active === i ? 'translate-x-1' : ''
                        }`}
                      >
                        {v.en}
                      </span>
                      <span className="text-xs font-medium text-gray-600 md:text-[13px]">
                        / {v.jp}
                      </span>
                      <ArrowRight
                        size={16}
                        className={`ml-auto shrink-0 transition-all duration-500 ease-expo ${
                          active === i
                            ? 'translate-x-0 text-live opacity-100'
                            : '-translate-x-2 text-ink opacity-30'
                        }`}
                        aria-hidden="true"
                      />
                    </div>
                    <p className="mt-2 pl-9 text-[13px] leading-relaxed text-gray-600 md:pl-11">
                      {v.desc}
                    </p>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex items-center justify-between gap-6">
              <p className="text-sm text-gray-600">代表メッセージ・募集要項・働き方は採用サイトへ。</p>
              <TextLink to="/careers" className="shrink-0" cursorLabel="JOIN">
                Careers Site
              </TextLink>
            </div>
          </div>

          {/* 右: 行ホバー連動の建築イメージ */}
          <div className="relative col-span-12 hidden lg:col-span-6 lg:block">
            <div className="sticky top-[104px] ml-auto aspect-[4/3] w-full max-w-[720px] overflow-hidden xl:mr-[-56px] 2xl:mr-0">
              <AnimatePresence initial={false}>
                <motion.div
                  key={rows[active].img}
                  className="absolute inset-0"
                  initial={reduce ? { opacity: 0 } : { clipPath: 'inset(100% 0 0 0)' }}
                  animate={reduce ? { opacity: 1 } : { clipPath: 'inset(0% 0 0 0)' }}
                  exit={{ opacity: 1 }}
                  transition={{ duration: 0.8, ease: EASE_QUINT }}
                >
                  <LivingImage
                    src={rows[active].img}
                    alt="緑のパネルが差し込まれたコンクリート建築"
                    mode="lit"
                    radius={260}
                    curtain={false}
                    sweep
                    className="h-full w-full"
                  />
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};

export default CareersSection;
